// De proefrit: showroomafspraken via het Renoco-portaal.
// Twee standen, bepaald door de server (zie ShowroomBookingController): "direct" boekt live in de agenda,
// "aanvraag" biedt vaste tijden aan en een medewerker belt om te bevestigen.
// Eén store voor de hele site; elke "Afspraak maken"-knop opent hetzelfde
// paneel, eventueel met een dag en tijd al gekozen (vanuit de hero).

const KORTE_DAG = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', timeZone: 'Europe/Amsterdam' });
const DAG_NUMMER = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', timeZone: 'Europe/Amsterdam' });
const KORTE_MAAND = new Intl.DateTimeFormat('nl-NL', { month: 'short', timeZone: 'Europe/Amsterdam' });
const LANGE_DATUM = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Amsterdam' });

function datumVan(iso) {
    // Middag gebruiken voorkomt dat een tijdzone de datum een dag verschuift.
    return new Date(`${iso}T12:00:00Z`);
}

function relatief(iso) {
    const vandaag = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());
    const morgenDatum = new Date(`${vandaag}T12:00:00Z`);
    morgenDatum.setUTCDate(morgenDatum.getUTCDate() + 1);
    const morgen = morgenDatum.toISOString().slice(0, 10);

    if (iso === vandaag) return 'Vandaag';
    if (iso === morgen) return 'Morgen';
    return null;
}

function icsTekst(waarde) {
    return String(waarde).replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

export default function maakBoeking() {
    return {
        open: false,
        status: 'idle', // idle | laden | klaar | mislukt
        dagen: [],
        modus: null, // null (nog niet geladen) | direct | aanvraag
        dag: null,
        tijd: null,
        stap: 'moment', // moment | gegevens | bevestigd
        bezig: false,
        fout: '',
        interesses: [],
        form: { first_name: '', last_name: '', email: '', phone: '', message: '' },
        bevestigd: null,
        bron: '',
        _laadBelofte: null,
        _voorkeurToegepast: false,

        laad() {
            if (this.status === 'klaar' || this._laadBelofte) {
                return this._laadBelofte ?? Promise.resolve();
            }

            this.status = 'laden';
            this._laadBelofte = fetch('/showroom-booking/slots', { headers: { Accept: 'application/json' } })
                .then((response) => response.json().then((data) => ({ response, data })))
                .then(({ response, data }) => {
                    const dagen = response.ok && data.success ? (data.days ?? []) : [];
                    if (response.ok && data.success) {
                        this.modus = data.mode === 'aanvraag' ? 'aanvraag' : 'direct';
                    }
                    this.dagen = dagen.filter((dag) => Array.isArray(dag.slots) && dag.slots.length > 0);
                    this.status = this.dagen.length ? 'klaar' : 'mislukt';
                })
                .catch(() => {
                    this.status = 'mislukt';
                })
                .finally(() => {
                    this._laadBelofte = null;
                });

            return this._laadBelofte;
        },

        herlaad() {
            this.status = 'idle';
            this.dagen = [];
            return this.laad();
        },

        // De eerstvolgende vrije momenten: per dag het eerste moment en een middagmoment,
        // zodat de bezoeker spreiding ziet in plaats van één volle ochtend.
        eerstvolgende(aantal = 4) {
            const lijst = [];
            const voegToe = (dag, slot) => lijst.push({
                datum: dag.date,
                tijd: slot.time,
                dagLabel: this.dagKort(dag.date),
                label: `${this.dagKort(dag.date)} ${slot.time}`,
                voluit: `${this.dagLang(dag.date)} om ${slot.time}`,
            });

            for (const dag of this.dagen) {
                if (lijst.length >= aantal) break;
                const eerste = dag.slots[0];
                voegToe(dag, eerste);

                const middag = dag.slots.find((slot) => slot.time >= '13:00' && slot.time !== eerste.time);
                if (middag && lijst.length < aantal) {
                    voegToe(dag, middag);
                }
            }

            return lijst.slice(0, aantal);
        },

        dagKort(iso) {
            const rel = relatief(iso);
            if (rel) return rel;
            const datum = datumVan(iso);
            return `${KORTE_DAG.format(datum).replace('.', '')} ${DAG_NUMMER.format(datum)} ${KORTE_MAAND.format(datum).replace('.', '')}`;
        },

        dagWeek(iso) {
            const rel = relatief(iso);
            return rel ?? KORTE_DAG.format(datumVan(iso)).replace('.', '');
        },

        dagNummer(iso) {
            return DAG_NUMMER.format(datumVan(iso));
        },

        dagMaand(iso) {
            return KORTE_MAAND.format(datumVan(iso)).replace('.', '');
        },

        dagLang(iso) {
            return LANGE_DATUM.format(datumVan(iso));
        },

        // In de stand "aanvraag" staat de afspraak nog niet vast: een medewerker belt om te bevestigen.
        get aanvraag() {
            return this.modus === 'aanvraag';
        },

        get gekozenDag() {
            return this.dagen.find((dag) => dag.date === this.dag) ?? null;
        },

        get samenvatting() {
            return this.dag && this.tijd ? `${this.dagLang(this.dag)} om ${this.tijd}` : '';
        },

        openen(opties = {}) {
            this.bron = opties.bron ?? '';
            this.fout = '';

            if (this.stap === 'bevestigd') {
                this.reset();
            }

            this.open = true;
            this.voorkeurToepassen();

            this.laad().then(() => {
                if (opties.datum && this.dagen.some((dag) => dag.date === opties.datum)) {
                    this.dag = opties.datum;
                    this.tijd = opties.tijd ?? null;
                    this.stap = this.tijd ? 'gegevens' : 'moment';
                } else if (!this.dag && this.dagen.length) {
                    this.dag = this.dagen[0].date;
                }
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'showroom_afspraak_open', bron: this.bron || 'knop' });
        },

        // Voor een boekingsblok dat inline op de pagina staat (showroompagina).
        klaarzetten() {
            this.voorkeurToepassen();

            return this.laad().then(() => {
                if (!this.dag && this.dagen.length) {
                    this.dag = this.dagen[0].date;
                }
            });
        },

        sluiten() {
            this.open = false;
            if (this.stap === 'bevestigd') {
                setTimeout(() => this.reset(), 400);
            }
        },

        kiesDag(iso) {
            this.dag = iso;
            this.tijd = null;
        },

        kiesTijd(tijd) {
            this.tijd = tijd;
            this.stap = 'gegevens';
        },

        terugNaarMoment() {
            this.stap = 'moment';
            this.fout = '';
        },

        wisselInteresse(product) {
            const index = this.interesses.indexOf(product);
            if (index === -1) {
                this.interesses.push(product);
                return;
            }

            this.interesses.splice(index, 1);
        },

        // Op een productpagina staat dat product (en de groep) al aangevinkt.
        voorkeurToepassen() {
            if (this._voorkeurToegepast) return;
            this._voorkeurToegepast = true;

            const namen = [...document.querySelectorAll('[data-productkeuze] [data-voorkeur]')]
                .map((el) => el.dataset.product)
                .filter(Boolean);

            for (const naam of namen) {
                if (!this.interesses.includes(naam)) {
                    this.interesses.push(naam);
                }
            }
        },

        reset() {
            this.stap = 'moment';
            this.tijd = null;
            this.fout = '';
            this.bevestigd = null;
            this.interesses = [];
            this._voorkeurToegepast = false;
            this.form = { first_name: '', last_name: '', email: '', phone: '', message: '' };
        },

        async boek() {
            if (!this.dag || !this.tijd || this.bezig) return;

            // Onder volledige statische caching ververst Statamic het token in de meta-tag.
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content ?? '';

            this.bezig = true;
            this.fout = '';

            const bericht = [
                this.interesses.length ? `Interesse: ${this.interesses.join(', ')}.` : '',
                this.form.message.trim(),
                typeof window !== 'undefined' ? `Geboekt via: ${window.location.pathname}` : '',
            ].filter(Boolean).join(' ').slice(0, 1000);

            try {
                const response = await fetch('/showroom-booking/book', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrf,
                    },
                    body: JSON.stringify({
                        date: this.dag,
                        time: this.tijd,
                        ...this.form,
                        message: bericht,
                        // Los meegestuurd voor de stand "aanvraag": zo komt het netjes in de lead te staan.
                        note: this.form.message.trim(),
                        interests: this.interesses,
                        page: window.location.pathname,
                    }),
                });
                const data = await response.json().catch(() => ({}));

                if (response.ok && data.success) {
                    const aanvraag = data.mode === 'aanvraag';

                    this.bevestigd = { datum: this.dag, tijd: this.tijd, naam: this.form.first_name, aanvraag };
                    this.stap = 'bevestigd';

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'showroom_afspraak_geboekt', bron: this.bron || 'knop', modus: aanvraag ? 'aanvraag' : 'direct' });

                    // Direct geboekt: het tijdslot is weg; volgende keer verse beschikbaarheid.
                    if (!aanvraag) {
                        this.status = 'idle';
                        this.dagen = [];
                    }
                    return;
                }

                if (response.status === 422 && data.errors) {
                    this.fout = Object.values(data.errors).flat()[0] ?? 'Controleer uw gegevens.';
                } else {
                    this.fout = data.message ?? 'Dit tijdstip is net vergeven. Kies een ander moment.';
                }

                // Een vergeven of ongeldig tijdslot: terug naar het moment, met verse beschikbaarheid.
                const slotWeg = response.status === 422
                    && (data.errors?.time || data.errors?.date || /geboekt|bezet|beschikbaar|tijdslot/i.test(data.message ?? ''));

                if (slotWeg) {
                    this.stap = 'moment';
                    this.tijd = null;
                    await this.herlaad();
                }
            } catch (e) {
                this.fout = 'Boeken lukt nu niet. Bel ons gerust op 046 - 808 01 17, dan plannen we het samen in.';
            } finally {
                this.bezig = false;
            }
        },

        // Download via een Blob: betrouwbaarder dan een data-URI op de telefoon.
        downloadAgenda() {
            const inhoud = decodeURIComponent(this.agendaLink().split(',')[1] ?? '');
            if (!inhoud) return;

            const blob = new Blob([inhoud], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'renoco-showroomafspraak.ics';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        },

        agendaLink() {
            if (!this.bevestigd) return '#';

            const [uur, minuut] = this.bevestigd.tijd.split(':').map(Number);
            const datum = this.bevestigd.datum.replace(/-/g, '');
            const start = `${datum}T${String(uur).padStart(2, '0')}${String(minuut || 0).padStart(2, '0')}00`;
            const eindUur = uur + 1;
            const eind = `${datum}T${String(eindUur).padStart(2, '0')}${String(minuut || 0).padStart(2, '0')}00`;

            const regels = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Renoco//Showroomafspraak//NL',
                'BEGIN:VEVENT',
                `UID:${datum}-${uur}${minuut}-${Date.now()}@renoco.nl`,
                `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`,
                `DTSTART;TZID=Europe/Amsterdam:${start}`,
                `DTEND;TZID=Europe/Amsterdam:${eind}`,
                `SUMMARY:${icsTekst('Showroomafspraak Renoco')}`,
                `LOCATION:${icsTekst('Renoco showroom, Economiestraat 1A, 6135 KV Sittard')}`,
                `DESCRIPTION:${icsTekst("Neem foto's en globale maten van uw woning mee. Gratis parkeren voor de deur.")}`,
                'END:VEVENT',
                'END:VCALENDAR',
            ];

            return `data:text/calendar;charset=utf-8,${encodeURIComponent(regels.join('\r\n'))}`;
        },
    };
}
